import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, AlertTriangle, Truck, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { ClientPickerOrCreate } from '@/components/common/ClientPickerOrCreate';
import { CityPickerOrCreate, type CityOption } from '@/components/common/CityPickerOrCreate';
import { VehiclePicker, type VehicleOption } from '@/components/common/VehiclePicker';
import { DriverPicker } from '@/components/common/DriverPicker';
import { useRef } from 'react';

interface Vehicle { id: string; plateEn: string | null; code?: string | null; vehicle_type?: string }
interface Driver  { id: string; fullNameEn: string | null; code?: string | null }

export default function NewFlatbedTripPage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);

  const [clientId, setClientId] = useState<string | null>(null);
  const [clientReference, setClientReference] = useState('');

  const [pickupCityId, setPickupCityId] = useState<string | null>(null);
  const [pickupCity, setPickupCity] = useState<CityOption | null>(null);
  const [pickupLocation, setPickupLocation] = useState('');

  const [deliveryCityId, setDeliveryCityId] = useState<string | null>(null);
  const [deliveryCity, setDeliveryCity] = useState<CityOption | null>(null);
  const [deliveryLocation, setDeliveryLocation] = useState('');

  const [routeDistanceKm, setRouteDistanceKm] = useState<string>('');
  const [distanceSource, setDistanceSource] = useState<'curated' | 'haversine' | 'manual' | 'unknown' | null>(null);
  const [distanceLoading, setDistanceLoading] = useState(false);

  const [containerType, setContainerType] = useState('40ft');
  const [containerNumber, setContainerNumber] = useState('');
  const [estimatedWeightKg, setEstimatedWeightKg] = useState('');
  const [rateSar, setRateSar] = useState('');
  const [scheduledPickupAt, setScheduledPickupAt] = useState('');
  const [scheduledDeliveryAt, setScheduledDeliveryAt] = useState('');
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [driverId, setDriverId] = useState<string | null>(null);
  const [recommendedDriverId, setRecommendedDriverId] = useState<string | null>(null);
  const [autoFilledDriver, setAutoFilledDriver] = useState(false);
  const driverManualRef = useRef(false);
  const [advanceSar, setAdvanceSar] = useState('0');
  const [incentiveSar, setIncentiveSar] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  // PR-AFAQY-FUEL-XREF MAY 12 — diesel cost estimate based on the picked
  // vehicle's historical L/100km × distance × current SAR/liter.
  const [fuelEstimate, setFuelEstimate] = useState<{
    estimatedSar: number | null;
    estimatedLiters: number | null;
    recipeSource: 'vehicle_history' | 'vehicle_type_average' | 'global_average' | 'vehicle_type_default' | 'none';
    confidence: 'high' | 'medium' | 'low' | 'none';
    litersPer100km?: number;
    sarPerLiter?: number;
    note: string | null;
  } | null>(null);
  const [fuelEstimateBusy, setFuelEstimateBusy] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient.get('/api/v1/vehicles?status=active&pageSize=200').then((r) => {
      const d: unknown = r.data;
      let arr: Vehicle[] = [];
      if (Array.isArray(d)) arr = d as Vehicle[];
      else if (d && typeof d === 'object' && 'items' in d) arr = ((d as { items?: Vehicle[] }).items) ?? [];
      setVehicles(arr);
    }).catch(() => setVehicles([]));
    apiClient.get('/api/v1/drivers?status=active&pageSize=200').then((r) => {
      const d: unknown = r.data;
      if (Array.isArray(d)) setDrivers(d as Driver[]);
      else if (d && typeof d === 'object' && 'items' in d) setDrivers(((d as { items?: Driver[] }).items) ?? []);
    }).catch(() => setDrivers([]));
  }, []);

  // PR-AFAQY-FUEL-XREF MAY 12 — recompute the diesel estimate whenever the
  // picked vehicle or the route distance changes. Posts to the backend
  // which combines the vehicle's L/100km history with current SAR/liter.
  useEffect(() => {
    if (!vehicleId || !routeDistanceKm) { setFuelEstimate(null); return; }
    const km = Number(routeDistanceKm);
    if (!Number.isFinite(km) || km <= 0) { setFuelEstimate(null); return; }
    let cancelled = false;
    setFuelEstimateBusy(true);
    apiClient.post(`/api/v1/vehicles/${vehicleId}/estimate-trip-cost`, { distanceKm: km })
      .then((r) => { if (!cancelled) setFuelEstimate(r.data as typeof fuelEstimate); })
      .catch(() => { if (!cancelled) setFuelEstimate(null); })
      .finally(() => { if (!cancelled) setFuelEstimateBusy(false); });
    return () => { cancelled = true; };
  }, [vehicleId, routeDistanceKm]);

  // PR-RIYADH-FIX-2 MAY 12 — auto-fill distance when both cities are picked.
  // Curated value wins, Haversine fallback otherwise. User can override.
  useEffect(() => {
    if (!pickupCityId || !deliveryCityId) return;
    if (pickupCityId === deliveryCityId) { setRouteDistanceKm('0'); setDistanceSource('manual'); return; }
    let cancelled = false;
    setDistanceLoading(true);
    apiClient.get<{ distanceKm: number | null; source: string }>(
      `/api/v1/city-distances?from=${pickupCityId}&to=${deliveryCityId}`,
    )
      .then((r) => {
        if (cancelled) return;
        if (r.data.distanceKm != null) {
          setRouteDistanceKm(String(r.data.distanceKm));
          setDistanceSource(r.data.source as 'curated' | 'haversine');
        } else {
          setDistanceSource('unknown');
        }
      })
      .catch(() => { if (!cancelled) setDistanceSource('unknown'); })
      .finally(() => { if (!cancelled) setDistanceLoading(false); });
    return () => { cancelled = true; };
  }, [pickupCityId, deliveryCityId]);

  async function submit() {
    setError(null);
    if (!pickupCity || !deliveryCity || !rateSar) { setError('Pickup, delivery, and rate are required'); return; }
    setBusy(true);
    try {
      const r = await apiClient.post<{ id: string; trip_code: string }>('/api/v1/flatbed/trips', {
        clientId: clientId ?? undefined,
        clientReference: clientReference.trim() || undefined,
        pickupCity: pickupCity.nameEn,
        pickupLocationName: pickupLocation.trim() || undefined,
        deliveryCity: deliveryCity.nameEn,
        deliveryLocationName: deliveryLocation.trim() || undefined,
        routeDistanceKm: routeDistanceKm ? Number(routeDistanceKm) : undefined,
        rateSar: Number(rateSar),
        containerType: containerType || undefined,
        containerNumber: containerNumber.trim() || undefined,
        estimatedWeightKg: estimatedWeightKg ? Number(estimatedWeightKg) : undefined,
        scheduledPickupAt: scheduledPickupAt ? new Date(scheduledPickupAt).toISOString() : undefined,
        scheduledDeliveryAt: scheduledDeliveryAt ? new Date(scheduledDeliveryAt).toISOString() : undefined,
        internalNotes: internalNotes.trim() || undefined,
      });
      if (vehicleId && driverId) {
        await apiClient.post('/api/v1/flatbed/trips/' + r.data.id + '/assign', {
          vehicleId, driverId,
          driverAdvanceSar: Number(advanceSar) || 0,
          driverIncentiveSar: incentiveSar ? Number(incentiveSar) : undefined,
          scheduledPickupAt: scheduledPickupAt ? new Date(scheduledPickupAt).toISOString() : undefined,
          scheduledDeliveryAt: scheduledDeliveryAt ? new Date(scheduledDeliveryAt).toISOString() : undefined,
        });
      }
      navigate('/dashboard/flatbed/' + r.data.id);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard/flatbed"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Trips</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Truck className="h-4 w-4" /> New flatbed trip</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <Section title="Client">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Client">
              <ClientPickerOrCreate
                value={clientId}
                onChange={setClientId}
                scopes={['riyadh', 'all']}
                createDefaults={{ projectScope: 'riyadh', isOneTime: true }}
              />
            </Field>
            <Field label="Client reference"><Input value={clientReference} onChange={(e) => setClientReference(e.target.value)} placeholder="e.g. PO-12345" /></Field>
          </div>
        </Section>

        <Section title="Route + commercial">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Pickup city">
              <CityPickerOrCreate
                value={pickupCityId}
                onChange={(id, c) => { setPickupCityId(id); setPickupCity(c); }}
                placeholder="— Pickup —"
              />
            </Field>
            <Field label="Delivery city">
              <CityPickerOrCreate
                value={deliveryCityId}
                onChange={(id, c) => { setDeliveryCityId(id); setDeliveryCity(c); }}
                placeholder="— Delivery —"
              />
            </Field>
            <Field label="Pickup location"><Input value={pickupLocation} onChange={(e) => setPickupLocation(e.target.value)} placeholder="warehouse, port, etc." /></Field>
            <Field label="Delivery location"><Input value={deliveryLocation} onChange={(e) => setDeliveryLocation(e.target.value)} placeholder="warehouse, port, etc." /></Field>
            <Field label={
              <span className="inline-flex items-center gap-2">
                Distance (km)
                {distanceLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                {!distanceLoading && distanceSource === 'curated'  && <span className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Curated</span>}
                {!distanceLoading && distanceSource === 'haversine' && <span className="text-[10px] uppercase tracking-wider text-amber-600 dark:text-amber-400">Estimate</span>}
                {!distanceLoading && distanceSource === 'unknown'   && <span className="text-[10px] uppercase tracking-wider text-rose-600 dark:text-rose-400">Manual</span>}
              </span>
            }>
              <Input
                type="number"
                step="0.1"
                value={routeDistanceKm}
                onChange={(e) => { setRouteDistanceKm(e.target.value); setDistanceSource('manual'); }}
                placeholder="auto from cities"
              />
            </Field>
            <Field label="Rate (SAR)">
              <Input type="number" step="0.01" value={rateSar} onChange={(e) => setRateSar(e.target.value)} placeholder="e.g. 4200" />
            </Field>
            <Field label="Container type">
              <select value={containerType} onChange={(e) => setContainerType(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                <option value="20ft">20 ft</option>
                <option value="40ft">40 ft</option>
                <option value="40hc">40 ft HC</option>
                <option value="flatbed">Flatbed only</option>
              </select>
            </Field>
            <Field label="Container number"><Input value={containerNumber} onChange={(e) => setContainerNumber(e.target.value)} placeholder="ABCD1234567" /></Field>
            <Field label="Estimated weight (kg)"><Input type="number" value={estimatedWeightKg} onChange={(e) => setEstimatedWeightKg(e.target.value)} /></Field>
          </div>
          {distanceSource === 'haversine' && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 mt-2">
              <Calculator className="h-3 w-3" /> Haversine estimate from city coordinates — adjust if the actual road route is longer.
            </div>
          )}
        </Section>

        <Section title="Schedule">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Scheduled pickup"><Input type="datetime-local" value={scheduledPickupAt} onChange={(e) => setScheduledPickupAt(e.target.value)} /></Field>
            <Field label="Scheduled delivery"><Input type="datetime-local" value={scheduledDeliveryAt} onChange={(e) => setScheduledDeliveryAt(e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Assign vehicle + driver (optional, can defer)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Vehicle">
              <VehiclePicker
                value={vehicleId}
                preferProjectScope="riyadh"
                onChange={(id, v: VehicleOption | null) => {
                  setVehicleId(id);
                  const cd = v?.currentDriver?.id ?? null;
                  setRecommendedDriverId(cd);
                  if (!id) { driverManualRef.current = false; setAutoFilledDriver(false); return; }
                  if (!driverManualRef.current && cd && cd !== driverId) {
                    setDriverId(cd);
                    setAutoFilledDriver(true);
                  }
                }}
                placeholder="— Pick later —"
              />
            </Field>
            <Field label={
              <span className="inline-flex items-center gap-2">
                Driver
                {autoFilledDriver && (
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                    auto · click to change
                  </span>
                )}
              </span>
            }>
              <DriverPicker
                value={driverId}
                preferProjectScope="riyadh"
                preferredDriverId={recommendedDriverId}
                onChange={(id) => {
                  setDriverId(id);
                  driverManualRef.current = id !== null;
                  if (id !== recommendedDriverId) setAutoFilledDriver(false);
                }}
                placeholder="— Pick later —"
              />
            </Field>
            <Field label="Driver advance (SAR)"><Input type="number" step="0.01" value={advanceSar} onChange={(e) => setAdvanceSar(e.target.value)} /></Field>
            <Field label="Driver incentive (SAR)"><Input type="number" step="0.01" value={incentiveSar} onChange={(e) => setIncentiveSar(e.target.value)} /></Field>
          </div>
          {/* PR-AFAQY-FUEL-XREF MAY 12 — diesel cost estimate from vehicle history */}
          {vehicleId && routeDistanceKm && fuelEstimate && (
            <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
              {fuelEstimateBusy ? (
                <span className="text-muted-foreground inline-flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Estimating fuel cost…
                </span>
              ) : fuelEstimate.estimatedSar != null ? (
                <div className="space-y-1">
                  <div className="font-medium">
                    Estimated diesel for {routeDistanceKm} km:
                    {' '}
                    <span className="text-emerald-700 dark:text-emerald-400 font-mono">
                      ~{fuelEstimate.estimatedSar.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} SAR
                    </span>
                    {' · '}
                    <span className="text-muted-foreground font-mono">
                      ~{fuelEstimate.estimatedLiters?.toLocaleString(undefined, { maximumFractionDigits: 0 })} L
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Based on{' '}
                    {fuelEstimate.recipeSource === 'vehicle_history'  && <span>this vehicle's last 90 days ({fuelEstimate.litersPer100km} L/100 km, {fuelEstimate.sarPerLiter} SAR/L)</span>}
                    {fuelEstimate.recipeSource === 'vehicle_type_average' && <span>fleet average for this vehicle type ({fuelEstimate.litersPer100km} L/100 km — vehicle has too few fills yet)</span>}
                    {fuelEstimate.recipeSource === 'global_average' && <span>global fleet average ({fuelEstimate.litersPer100km} L/100 km — no per-type data yet)</span>}
                    {fuelEstimate.recipeSource === 'vehicle_type_default' && <span>industry default for this vehicle type ({fuelEstimate.litersPer100km} L/100 km — no Afaqy km data yet to compute actuals)</span>}
                    {' · '}
                    confidence: <span className={
                      fuelEstimate.confidence === 'high'   ? 'text-emerald-700 dark:text-emerald-400'
                    : fuelEstimate.confidence === 'medium' ? 'text-amber-700 dark:text-amber-400'
                    :                                        'text-rose-700 dark:text-rose-400'
                    }>{fuelEstimate.confidence}</span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">
                  {fuelEstimate.note ?? 'No estimate available yet.'}
                </div>
              )}
            </div>
          )}
        </Section>

        <Section title="Notes">
          <Field label="Internal notes">
            <textarea rows={3} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
          </Field>
        </Section>

        {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>}

        <div className="flex justify-end gap-2">
          <Link to="/dashboard/flatbed"><Button variant="outline">Cancel</Button></Link>
          <Button onClick={submit} disabled={busy} className="gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create trip</Button>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
